import React, { memo, useMemo } from 'react'
import { Box, Grid, Typography, alpha, useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useSelector } from 'react-redux'
import { green, red } from '../../assets/colors'
import { Security } from '../../proto/generated/security'
import { OverviewWidgetResponse_FieldGroup, PerformancePeriod } from '../../proto/generated/overview-widget'
import { RootState } from '../../store'
import SecurityRange from './SecurityRange.tsx'
import SurveillanceIndicatorCard from './SurveillanceIndicatorCard'

const PERFORMANCE_PERIOD_ORDER = ['1W', '1M', '3M', '6M', 'YTD', '1Y'] as const

/** TradingView-style opacity ramp cutoff for performance tile backgrounds. */
const COLOR_INTENSITY_CUTOFF_PCT = 10
const BG_ALPHA_MIN = 0.1
const BG_ALPHA_MAX = 0.42

const useStyles = makeStyles()((theme) => ({
    tile: {
        borderRadius: 4,
        padding: theme.spacing(1, 1.25),
        minHeight: 52,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
    },
    tileNeutral: {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)'
    },
    tileLabel: {
        fontSize: 11,
        fontWeight: 500,
        color: theme.palette.text.secondary,
        lineHeight: 1.2
    },
    tileValue: {
        fontSize: 15,
        fontWeight: 400,
        lineHeight: 1.2,
        fontVariantNumeric: 'tabular-nums'
    }
}))

export type OverviewPerformanceSectionProps = {
    security: Security
    /** Defaults to `security` when omitted (screener uses display row for surveillance). */
    surveillanceSecurity?: Security
    fieldGroups: OverviewWidgetResponse_FieldGroup[]
}

const formatPerformanceValue = (value: number): string => {
    const sign = value > 0 ? '+' : ''
    return `${sign}${value.toFixed(2)}%`
}

const intensityAlpha = (value: number): number => {
    const t = Math.min(1, Math.abs(value) / COLOR_INTENSITY_CUTOFF_PCT)
    return BG_ALPHA_MIN + (BG_ALPHA_MAX - BG_ALPHA_MIN) * t
}

const PerformancePeriodTile = ({ period }: { period: PerformancePeriod }) => {
    const { classes, cx } = useStyles()
    const theme = useTheme()
    const hasValue = period.hasValue
    const isUp = hasValue && period.value > 0
    const isDown = hasValue && period.value < 0
    const baseColor = isUp ? green : isDown ? red : theme.palette.text.secondary
    const background =
        isUp || isDown ? alpha(baseColor, intensityAlpha(period.value)) : undefined

    return (
        <Box
            className={cx(classes.tile, !background && classes.tileNeutral)}
            sx={background ? { backgroundColor: background } : undefined}
        >
            <Typography className={classes.tileLabel}>{period.label}</Typography>
            <Typography className={classes.tileValue} sx={{ color: baseColor }}>
                {hasValue ? formatPerformanceValue(period.value) : '—'}
            </Typography>
        </Box>
    )
}

const PerformancePeriodGrid = ({ periods }: { periods: PerformancePeriod[] }) => {
    const orderedPeriods = useMemo(() => {
        const byId = new Map(periods.map((period) => [period.id, period]))
        return PERFORMANCE_PERIOD_ORDER.map(
            (id) => byId.get(id) ?? { id, label: id, value: 0, hasValue: false }
        )
    }, [periods])

    return (
        <Grid container spacing={1}>
            {orderedPeriods.map((period) => (
                <Grid key={period.id} item xs={4}>
                    <PerformancePeriodTile period={period} />
                </Grid>
            ))}
        </Grid>
    )
}

const OverviewPerformanceSection = ({
    security,
    surveillanceSecurity,
    fieldGroups
}: OverviewPerformanceSectionProps) => {
    const performance = useSelector((state: RootState) => state.overviewStore.performance)

    return (
        <Box sx={{ px: 2, py: 1 }}>
            <PerformancePeriodGrid periods={performance} />
            <Box sx={{ mt: 2 }}>
                <SurveillanceIndicatorCard security={surveillanceSecurity ?? security} />
                <SecurityRange security={security} show52WkRange fieldGroups={fieldGroups} />
            </Box>
        </Box>
    )
}

const overviewPerformanceSectionPropsAreEqual = (
    prev: OverviewPerformanceSectionProps,
    next: OverviewPerformanceSectionProps
) =>
    prev.fieldGroups === next.fieldGroups &&
    prev.security?.exchange === next.security?.exchange &&
    prev.security?.code === next.security?.code &&
    (prev.surveillanceSecurity?.exchange ?? prev.security?.exchange) ===
        (next.surveillanceSecurity?.exchange ?? next.security?.exchange) &&
    (prev.surveillanceSecurity?.code ?? prev.security?.code) ===
        (next.surveillanceSecurity?.code ?? next.security?.code)

export default memo(OverviewPerformanceSection, overviewPerformanceSectionPropsAreEqual)
